import React from 'react';

import StrapiImageSource from './StrapiImageSource';
import { IStrapiMedia, IStrapiMediaFluidSize, IStrapiMediaFormatSizes } from 'types';

interface IProps {
  image: IStrapiMedia;
  sizes?: IStrapiMediaFluidSize[];
  [key: string]: unknown;
}

/**
 * Provides code for a fluid image. A fluid image is one that renders the same
 * image, but at different sizes, based on the width of the viewport. This
 * uses IStrapiMedia.formats for rendering the different image sizes at different
 * viewport widths
 *
 * Strapi will only generate image versions for widths that are less than the width
 * of the original image. So, if an original image is 1000px wide, Strapi won't
 * create a "large" format. Because of this, after we loop through and render all
 * the source tags for each image format, we add on another StrapiImageSource for
 * the original image so that we can get webP and 2x options for the original image.
 * @param props
 */
const StrapiImageFluid: React.FunctionComponent<IProps> = ({ image, sizes, ...rest }) => {
  const imgSrcSizes: IStrapiMediaFluidSize[] = sizes || [
    { maxWidth: 245, preferredFormat: IStrapiMediaFormatSizes.THUMBNAIL },
    { maxWidth: 500, preferredFormat: IStrapiMediaFormatSizes.SMALL },
    { maxWidth: 750, preferredFormat: IStrapiMediaFormatSizes.MEDIUM },
    { maxWidth: 1000, preferredFormat: IStrapiMediaFormatSizes.LARGE }
  ];

  let imageFormatSourceElements: React.ReactNodeArray | null = null;
  if (image.formats) {
    imageFormatSourceElements = imgSrcSizes.map(({ maxWidth, preferredFormat }) => {
      return image.formats && image.formats[preferredFormat] ? (
        <StrapiImageSource
          key={preferredFormat}
          image={image.formats[preferredFormat]}
          media={`(max-width: ${maxWidth}px)`}
        />
      ) : null;
    });
  }

  return (
    <picture>
      {imageFormatSourceElements}
      <StrapiImageSource key="default" image={image} />
      <img src={image.url} alt={image.alternativeText || ''} {...rest} />
    </picture>
  );
};

export default StrapiImageFluid;
